import { config } from '../config';
import { apiEndpoint } from '../config/paths';

/**
 * API endpoint helper function - use this directly with PATH_SEGMENTS
 * This is the single source of truth for building API URLs
 * 
 * Basic Usage:
 * - api(PATH_SEGMENTS.INTERACTION, id) -> http://localhost:8000/interaction/123
 * - api(PATH_SEGMENTS.AUTH, PATH_SEGMENTS.LOGIN) -> http://localhost:8000/auth/login
 * - api(PATH_SEGMENTS.EVALFORM, formId, PATH_SEGMENTS.SECTION) -> http://localhost:8000/evalform/5/section
 * 
 * Query Parameters Patterns:
 * 
 * Pattern 1: Single query parameter (simple string concatenation)
 *   `${api(PATH_SEGMENTS.TRANSCRIPT, id)}?listing=true`
 *   Result: "http://localhost:8000/transcript/123?listing=true"
 * 
 * Pattern 2: Multiple query parameters (template literal)
 *   `${api(PATH_SEGMENTS.EVALUATION, id)}?scores=true&all=true&insights=true`
 *   Result: "http://localhost:8000/evaluation/456?scores=true&all=true&insights=true"
 * 
 * Pattern 3: Using URLSearchParams (recommended for multiple params)
 *   const params = new URLSearchParams();
 *   params.set('page', '1');
 *   params.set('limit', '10');
 *   `${api(PATH_SEGMENTS.INTERACTION)}?${params.toString()}`
 *   Result: "http://localhost:8000/interaction?page=1&limit=10"
 * 
 * Pattern 4: Query parameter with encoded values
 *   `${api(PATH_SEGMENTS.MOMENT)}?name=${encodeURIComponent(name)}`
 *   Result: "http://localhost:8000/moment?name=My%20Moment%20Name"
 * 
 * Pattern 5: Query parameter with path segments + ID
 *   `${api(PATH_SEGMENTS.KPI, kpiName)}/?iid=${interactionId}`
 *   Result: "http://localhost:8000/kpi/call_score/?iid=123"
 * 
 * For navigation, use route() from '../config/paths' with the same PATH_SEGMENTS
 * Example: route(PATH_SEGMENTS.AUTH, PATH_SEGMENTS.LOGIN) -> /auth/login

 */
export const api = apiEndpoint(config.api.baseUrl);


// Add this type definition at the top of the file
export interface ApiError extends Error {
  response?: Response;
}

/**
 * Extracted error information from API error response
 */
export interface ExtractedError {
  fieldErrors: Record<string, string[]>; // Field-specific validation errors
  message: string; // Single error message (formatted from fieldErrors or direct message)
}

/**
 * Extracts detailed error information from an API error.
 * Handles different error response formats:
 * - { "detail": "Specific error message" } -> returns message
 * - { "detail": { "field": ["error1", "error2"] } } -> returns fieldErrors and formatted message
 * - { "error": "Specific error message" } -> returns message
 * - { "error": { "field": ["error1", "error2"] } } -> returns fieldErrors and formatted message
 * - { "message": "Error message" } -> returns message
 * 
 * Note: Both "detail" and "error" fields are handled identically and can contain
 * either a string message or an object with field-specific validation errors.
 * 
 * @param err - The error object (ApiError or Error)
 * @returns ExtractedError with fieldErrors and message
 */
export async function extractApiError(err: unknown): Promise<ExtractedError> {
  const result: ExtractedError = {
    fieldErrors: {},
    message: 'An error occurred'
  };

  if (!(err instanceof Error)) {
    result.message = 'An unknown error occurred';
    return result;
  }

  const apiError = err as ApiError;
  
  // If error has no response object, use the error message
  if (!apiError.response) {
    result.message = err.message || 'An error occurred';
    return result;
  }

  try {
    // Read the response body (response can only be read once)
    const errorData = await apiError.response.json();
    
    // Helper function to extract field errors and message from a field value
    const extractFromField = (fieldValue: any): { fieldErrors: Record<string, string[]>; message: string } => {
      const fieldErrors: Record<string, string[]> = {};
      const fieldErrorMessages: string[] = [];
      
      if (typeof fieldValue === 'string') {
        // Simple error message: { "detail": "Specific error message" } or { "error": "Specific error message" }
        return { fieldErrors: {}, message: fieldValue };
      } else if (typeof fieldValue === 'object' && fieldValue !== null) {
        // Field-specific errors: { "detail": { "field": ["error1", "error2"] } } or { "error": { "field": ["error1", "error2"] } }
        Object.entries(fieldValue).forEach(([field, messages]) => {
          if (Array.isArray(messages)) {
            fieldErrors[field] = messages;
            fieldErrorMessages.push(`${field}: ${messages.join(', ')}`);
          } else if (typeof messages === 'string') {
            fieldErrors[field] = [messages];
            fieldErrorMessages.push(`${field}: ${messages}`);
          }
        });
        
        return {
          fieldErrors,
          message: fieldErrorMessages.length > 0 
            ? fieldErrorMessages.join('; ') 
            : 'Validation error occurred'
        };
      }
      
      return { fieldErrors: {}, message: String(fieldValue) };
    };
    
    // Handle detail field (most common in FastAPI/backend responses).
    // If detail is an empty object, prefer top-level message/error instead.
    const detailHasContent =
      errorData.detail !== undefined &&
      errorData.detail !== null &&
      (typeof errorData.detail !== 'object' || Object.keys(errorData.detail).length > 0);
    if (detailHasContent) {
      const extracted = extractFromField(errorData.detail);
      result.fieldErrors = extracted.fieldErrors;
      result.message = extracted.message;
    } else if (errorData.error) {
      // Handle error field (same as detail field)
      const extracted = extractFromField(errorData.error);
      result.fieldErrors = extracted.fieldErrors;
      result.message = extracted.message;
    } else if (errorData.message) {
      // Try message field (always treated as string)
      result.message = typeof errorData.message === 'string' 
        ? errorData.message 
        : String(errorData.message);
    } else {
      // Fall back to HTTP status message
      result.message = `HTTP ${apiError.response.status}: ${apiError.response.statusText || err.message}`;
    }
  } catch (parseError) {
    // If we can't parse the error response, use the original error message
    console.error('Error parsing error response:', parseError);
    result.message = err.message || 'An error occurred';
  }

  return result;
}

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

export async function fetchData<T>(url: string): Promise<T> {
  try {
    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = new Error(`HTTP error! status: ${response.status}`) as ApiError;
      error.response = response;
      throw error;
    }
    return await response.json() as T;
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
}

export async function postData<T>(
  url: string, 
  data: any, 
  method: string = 'POST',
  contentType: string = 'application/json'
): Promise<T> {
  try {
    const isFormData = data instanceof FormData;
    
    // For FormData, don't set Content-Type - browser will set it with boundary
    // For other content types, get auth headers which include Content-Type
    const headers: Record<string, string> = isFormData 
      ? {} // Start with empty headers for FormData
      : { ...getAuthHeaders() };

    // Add auth token if available (for FormData, we need to add it manually)
    if (isFormData) {
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    // Only set Content-Type for non-FormData
    if (!isFormData && contentType !== 'multipart/form-data') {
      headers['Content-Type'] = contentType;
    }

    const body = isFormData
      ? data
      : contentType === 'application/json'
        ? JSON.stringify(data)
        : data;

    const response = await fetch(url, {
      method: method,
      headers,
      body: body,
    });

    if (!response.ok) {
      const error = new Error(`HTTP error! status: ${response.status}`) as ApiError;
      error.response = response;
      throw error;
    }
    
    return await response.json() as T;
  } catch (error) {
    console.error("Post error:", error);
    throw error;
  }
}

export async function deleteData<T>(url: string): Promise<T> {
  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = new Error(`HTTP error! status: ${response.status}`) as ApiError;
      error.response = response;
      throw error;
    }
    
    // Handle case where DELETE returns no content
    if (response.status === 204) {
      return {} as T;
    }
    
    return await response.json() as T;
  } catch (error) {
    console.error("Delete error:", error);
    throw error;
  }
} 